// Registry of tool loaders.
//
// Each entry is a () => import(...) so esbuild emits a separate chunk per
// tool — visiting any one /free-tools/<slug> doesn't download the JS for any
// other tool, and heavy libraries (marked, diff, qrcode, etc.) are only
// fetched when the tool that uses them is loaded.

export const tools = {
  // Image format conversion
  "png-to-jpg": () => import("./png-to-jpg.js"),
  "jpg-to-png": () => import("./jpg-to-png.js"),
  "png-to-webp": () => import("./png-to-webp.js"),
  "jpg-to-webp": () => import("./jpg-to-webp.js"),
  "webp-to-png": () => import("./webp-to-png.js"),
  "webp-to-jpg": () => import("./webp-to-jpg.js"),
  "png-to-avif": () => import("./png-to-avif.js"),
  "jpg-to-avif": () => import("./jpg-to-avif.js"),
  "bmp-to-png": () => import("./bmp-to-png.js"),
  "bmp-to-jpg": () => import("./bmp-to-jpg.js"),
  "svg-to-png": () => import("./svg-to-png.js"),
  "tiff-to-jpg": () => import("./tiff-to-jpg.js"),
  "tiff-to-png": () => import("./tiff-to-png.js"),
  "heic-to-jpg": () => import("./heic-to-jpg.js"),
  "heic-to-png": () => import("./heic-to-png.js"),
  "heic-to-webp": () => import("./heic-to-webp.js"),
  "png-to-ico": () => import("./png-to-ico.js"),
  "ico-to-png": () => import("./ico-to-png.js"),

  // Image sizing & cropping
  "image-resizer": () => import("./image-resizer.js"),
  "image-downscaler": () => import("./image-downscaler.js"),
  "image-cropper": () => import("./image-cropper.js"),
  "aspect-ratio-converter": () => import("./aspect-ratio-converter.js"),
  "image-padder": () => import("./image-padder.js"),
  "circle-crop": () => import("./circle-crop.js"),

  // Image compression
  "jpg-compressor": () => import("./jpg-compressor.js"),
  "png-compressor": () => import("./png-compressor.js"),
  "webp-compressor": () => import("./webp-compressor.js"),

  // Image editing & effects
  "image-rotator": () => import("./image-rotator.js"),
  "image-flipper": () => import("./image-flipper.js"),
  "brightness-contrast-saturation": () => import("./brightness-contrast-saturation.js"),
  "grayscale-converter": () => import("./grayscale-converter.js"),
  "sepia-filter": () => import("./sepia-filter.js"),
  "invert-colors": () => import("./invert-colors.js"),
  "blur-tool": () => import("./blur-tool.js"),
  "pixelate-tool": () => import("./pixelate-tool.js"),
  "sharpen-filter": () => import("./sharpen-filter.js"),
  "noise-grain": () => import("./noise-grain.js"),
  "duotone-generator": () => import("./duotone-generator.js"),
  "color-tint-overlay": () => import("./color-tint-overlay.js"),

  // Image generators & utilities
  "solid-color-generator": () => import("./solid-color-generator.js"),
  "gradient-image-generator": () => import("./gradient-image-generator.js"),
  "placeholder-image-generator": () => import("./placeholder-image-generator.js"),
  "favicon-generator": () => import("./favicon-generator.js"),
  "image-watermark-adder": () => import("./image-watermark-adder.js"),
  "side-by-side-combiner": () => import("./side-by-side-combiner.js"),
  "collage-maker": () => import("./collage-maker.js"),
  "photo-frame-adder": () => import("./photo-frame-adder.js"),
  "image-to-base64": () => import("./image-to-base64.js"),
  "base64-to-image": () => import("./base64-to-image.js"),

  // Image analysis
  "image-dimensions-checker": () => import("./image-dimensions-checker.js"),
  "color-picker-from-image": () => import("./color-picker-from-image.js"),
  "palette-extractor": () => import("./palette-extractor.js"),
  "image-histogram-viewer": () => import("./image-histogram-viewer.js"),

  // Image compression — bulk
  "bulk-image-compressor": () => import("./bulk-image-compressor.js"),
  "bulk-image-resizer": () => import("./bulk-image-resizer.js"),

  // Image metadata
  "exif-viewer": () => import("./exif-viewer.js"),
  "exif-stripper": () => import("./exif-stripper.js"),
  "image-metadata-editor": () => import("./image-metadata-editor.js"),

  // PDF tools
  "image-to-pdf": () => import("./image-to-pdf.js"),
  "pdf-to-image": () => import("./pdf-to-image.js"),
  "merge-pdf": () => import("./merge-pdf.js"),
  "split-pdf": () => import("./split-pdf.js"),
  "pdf-page-extractor": () => import("./pdf-page-extractor.js"),
  "reorder-pdf-pages": () => import("./reorder-pdf-pages.js"),
  "rotate-pdf-pages": () => import("./rotate-pdf-pages.js"),
  "delete-pdf-pages": () => import("./delete-pdf-pages.js"),
  "add-pdf-page-numbers": () => import("./add-pdf-page-numbers.js"),
  "add-pdf-watermark": () => import("./add-pdf-watermark.js"),
  "pdf-compressor": () => import("./pdf-compressor.js"),

  // QR & barcode
  "qr-code-generator": () => import("./qr-code-generator.js"),
  "wifi-qr-generator": () => import("./wifi-qr-generator.js"),
  "vcard-qr-generator": () => import("./vcard-qr-generator.js"),
  "barcode-generator": () => import("./barcode-generator.js"),
  "qr-code-scanner": () => import("./qr-code-scanner.js"),
  "barcode-scanner": () => import("./barcode-scanner.js"),

  // Color tools
  "hex-to-rgb": () => import("./hex-to-rgb.js"),
  "rgb-to-hex": () => import("./rgb-to-hex.js"),
  "hex-to-hsl": () => import("./hex-to-hsl.js"),
  "hsl-to-hex": () => import("./hsl-to-hex.js"),
  "color-palette-generator": () => import("./color-palette-generator.js"),
  "gradient-generator": () => import("./gradient-generator.js"),
  "wcag-contrast-checker": () => import("./wcag-contrast-checker.js"),
  "color-blindness-simulator": () => import("./color-blindness-simulator.js"),
  "random-color-generator": () => import("./random-color-generator.js"),

  // Text & data
  "json-formatter": () => import("./json-formatter.js"),
  "base64-encoder": () => import("./base64-encoder.js"),
  "base64-decoder": () => import("./base64-decoder.js"),
  "url-encoder": () => import("./url-encoder.js"),
  "url-decoder": () => import("./url-decoder.js"),
  "word-character-counter": () => import("./word-character-counter.js"),
  "lorem-ipsum-generator": () => import("./lorem-ipsum-generator.js"),
  "uuid-generator": () => import("./uuid-generator.js"),
  "hash-generator": () => import("./hash-generator.js"),
  "password-generator": () => import("./password-generator.js"),
  "text-case-converter": () => import("./text-case-converter.js"),
  "markdown-preview": () => import("./markdown-preview.js"),
  "diff-checker": () => import("./diff-checker.js"),

  // SEO & social cards
  "social-card-preview": () => import("./social-card-preview.js")
}
